/**
 * storage/photoPath.ts
 *
 * Client-side helpers for photo path handling.
 * Mirrors the server-side sanitizeFilename() in photo-upload-token/index.ts.
 *
 * These functions are pure (no I/O) and are unit-tested directly.
 * The canonical path builder runs server-side — this is for display and
 * local validation only.
 */

/**
 * Accepted MIME types AT REST in storage. Always image/jpeg post-processing,
 * but this set defines what `photo-upload-token` and the storage bucket allow.
 *
 * Input from the camera/file picker can be a wider set (including HEIC/HEIF) —
 * see ALLOWED_INPUT_MIME_TYPES below. Those are converted to image/jpeg by
 * processImage before they reach the upload path.
 */
export const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
] as const)

/**
 * MIME types accepted from the camera/file picker before processing.
 * iPhones default to HEIC; we convert client-side via heic-to so storage
 * never sees HEIC bytes.
 */
export const ALLOWED_INPUT_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const)

/** Maximum file size in bytes (15 MB). */
export const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024

/**
 * Sanitizes a filename: strips path traversal characters, normalizes the
 * extension to a safe value, limits base length to 64 characters.
 *
 * Mirrors sanitizeFilename() in supabase/functions/photo-upload-token/index.ts.
 * Must stay in sync with the server-side implementation.
 */
export function sanitizeFilename(raw: string): string {
  const parts   = raw.split('.')
  const rawExt  = parts.length > 1 ? parts.pop()!.toLowerCase() : 'jpg'
  const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(rawExt) ? rawExt : 'jpg'
  const base    = parts.join('').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64)
  return `${base}.${safeExt}`
}

/**
 * Returns true if the MIME type is accepted at storage (post-processing).
 */
export function isAllowedMimeType(contentType: string): boolean {
  return ALLOWED_MIME_TYPES.has(contentType as 'image/jpeg' | 'image/png' | 'image/webp')
}

/**
 * Returns true if the MIME type is accepted as input to the upload pipeline
 * (pre-processing). Includes HEIC/HEIF; processImage normalizes those to
 * image/jpeg before they reach the upload path.
 */
export function isAllowedInputMimeType(contentType: string): boolean {
  return ALLOWED_INPUT_MIME_TYPES.has(
    contentType as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/heic' | 'image/heif',
  )
}

/**
 * Returns true if the file size is within the allowed limit.
 */
export function isWithinSizeLimit(bytes: number): boolean {
  return bytes > 0 && bytes <= MAX_FILE_SIZE_BYTES
}
